import React from "react";
import { useSelector } from "react-redux";
import { WarehouseStaffApi } from "@/apis";
import type { IAuth } from "@/models/Auth";
import type { IWarehouseStaffRole, IWarehouseStaffScope } from "@/apis/warehouseStaff";

export interface IPerWarehousePermission {
  warehouseId: string;
  staffRole: IWarehouseStaffRole;
  scope: IWarehouseStaffScope;
  canManageStaff: boolean;
  isPrimary: boolean;
}

interface IContext {
  isManager: boolean;
  perWarehouseMap: Map<string, IPerWarehousePermission[]>;
  loading: boolean;
  refresh: () => Promise<void>;
}

const WarehouseStaffPermissionsContext = React.createContext<IContext>({
  isManager      : false,
  perWarehouseMap: new Map(),
  loading        : false,
  refresh        : async () => undefined
});

interface IProviderProps {
  children: React.ReactNode;
}

export function WarehouseStaffPermissionsProvider({ children }: IProviderProps) {
  const accessToken = useSelector((state: { auth: IAuth }) => state.auth.accessToken);
  const [isManager, setIsManager] = React.useState(false);
  const [perWarehouseMap, setPerWarehouseMap] = React.useState<Map<string, IPerWarehousePermission[]>>(new Map());
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!accessToken) {
      setIsManager(false);
      setPerWarehouseMap(new Map());
      return;
    }
    setLoading(true);
    try {
      const res = (await WarehouseStaffApi.mePermissions()) as {
        isManager: boolean;
        perWarehouse: IPerWarehousePermission[];
      };
      setIsManager(!!res?.isManager);
      const map = new Map<string, IPerWarehousePermission[]>();
      (res?.perWarehouse ?? []).forEach((row) => {
        const list = map.get(row.warehouseId) ?? [];
        list.push(row);
        map.set(row.warehouseId, list);
      });
      setPerWarehouseMap(map);
    } catch {
      setIsManager(false);
      setPerWarehouseMap(new Map());
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const value = React.useMemo<IContext>(
    () => ({ isManager, perWarehouseMap, loading, refresh }),
    [isManager, perWarehouseMap, loading, refresh]
  );

  return (
    <WarehouseStaffPermissionsContext.Provider value={value}>
      {children}
    </WarehouseStaffPermissionsContext.Provider>
  );
}

export function useWarehouseStaffPermissionsContext() {
  return React.useContext(WarehouseStaffPermissionsContext);
}
